import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class;
import { computed } from 'mobx';
import { timestampMs } from '@bufbuild/protobuf/wkt';
import { Model } from 'core/model/Model';
import { ALERT_SEVERITY_RANKS, SEVERITY_MAP } from '@kentik/ui-shared/alerting/constants';
import $alerting from 'app/stores/alerting/$alerting';
import { Severity } from '@kentik/schema/gen/ts/kentik/alerting/types/v202303/types_pb';
import { getCorrelationStatistics } from './correlationsApi';
import { toFilter } from './filters';
function severityKeyOf(severity) {
  return SEVERITY_MAP[Severity[severity]]?.ALERT_MANAGER;
}

/** Server-side aggregates over a filtered set of correlations, bucketed over time. */
let CorrelationStatisticsModel = (_class = class CorrelationStatisticsModel extends Model {
  constructor(...args) {
    super(...args);
    /** Only the latest request's response is applied, as in `CorrelationCollection`. */
    this.fetchSequence = 0;
  }
  get defaults() {
    return {
      stats: [],
      bucketMs: 0
    };
  }
  get summary() {
    const summary = this.get('summary');
    return {
      activeCount: summary?.activeCount ?? 0,
      clearedCount: Math.max(0, (summary?.totalCount ?? 0) - (summary?.activeCount ?? 0)),
      totalCount: summary?.totalCount ?? 0,
      totalCorrelatedAlerts: summary?.totalCorrelatedAlerts ?? 0,
      ackedCount: summary?.ackedCount ?? 0,
      unackedCount: summary?.unackedCount ?? 0,
      confidenceCounts: {
        High: summary?.highConfidenceCount ?? 0,
        Medium: summary?.mediumConfidenceCount ?? 0,
        Low: summary?.lowConfidenceCount ?? 0,
        Unscored: summary?.unspecifiedConfidenceCount ?? 0
      },
      severityCounts: Object.fromEntries((summary?.severityCounts ?? []).filter(({
        severity
      }) => severity !== Severity.UNSPECIFIED && severityKeyOf(severity)).map(({
        severity,
        count
      }) => [$alerting.getSeverityLabel(severityKeyOf(severity)), count]))
    };
  }

  /** One bar per bucket, with its correlations broken down by severity, worst first. */
  get timelineBuckets() {
    const bucketMs = this.get('bucketMs');
    const byStart = new Map();
    this.get('stats').forEach(({
      bucketStart,
      severity,
      correlationCount,
      correlatedAlertCount
    }) => {
      const severityKey = severityKeyOf(severity);
      if (!bucketStart || severity === Severity.UNSPECIFIED || !severityKey) {
        return;
      }
      const start = timestampMs(bucketStart);
      const shares = byStart.get(start) ?? new Map();
      const share = shares.get(severityKey) ?? {
        severityKey,
        label: $alerting.getSeverityLabel(severityKey),
        color: $alerting.getSeverityColor(severityKey),
        count: 0,
        alertCount: 0
      };
      share.count += correlationCount;
      share.alertCount += correlatedAlertCount;
      shares.set(severityKey, share);
      byStart.set(start, shares);
    });
    return [...byStart.entries()].sort(([a], [b]) => a - b).map(([start, shares]) => {
      const breakdown = [...shares.values()].sort((a, b) => ALERT_SEVERITY_RANKS[b.label] - ALERT_SEVERITY_RANKS[a.label]);
      return {
        start,
        end: start + bucketMs,
        count: breakdown.reduce((total, share) => total + share.count, 0),
        alertCount: breakdown.reduce((total, share) => total + share.alertCount, 0),
        severityKey: breakdown[0].severityKey,
        severityColor: breakdown[0].color,
        breakdown: breakdown.map(({
          severityKey,
          label,
          color,
          count
        }) => ({
          severityKey,
          label,
          color,
          count
        }))
      };
    });
  }

  /** Correlations counted in the summary that opened before the first bucket at or after `domainStart`. */
  carriedOverCount(domainStart) {
    const bucketMs = this.get('bucketMs');
    const snappedStart = bucketMs ? Math.floor(domainStart / bucketMs) * bucketMs : domainStart;
    const inWindow = this.timelineBuckets.filter(bucket => bucket.start >= snappedStart).reduce((total, bucket) => total + bucket.count, 0);
    return Math.max(0, this.summary.totalCount - inWindow);
  }
  async fetch({
    filter,
    granularity
  }) {
    this.fetchSequence += 1;
    const sequence = this.fetchSequence;
    this.setRequestStatus('fetching');
    try {
      const response = await getCorrelationStatistics({
        filter: toFilter(filter),
        bucket: granularity.interval
      });
      if (sequence === this.fetchSequence) {
        this.set({
          ...this.deserialize(response),
          bucketMs: granularity.ms
        });
        this.error = null;
      }
    } catch (error) {
      if (sequence === this.fetchSequence) {
        this.error = {
          label: 'fetching',
          body: error
        };
      }
      throw error;
    } finally {
      if (sequence === this.fetchSequence) {
        this.setRequestStatus(null);
      }
    }
  }
  deserialize({
    summary,
    stats
  }) {
    return {
      summary,
      stats
    };
  }
}, _applyDecoratedDescriptor(_class.prototype, "summary", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "summary"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "timelineBuckets", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "timelineBuckets"), _class.prototype), _class);
export { CorrelationStatisticsModel as default };